import { themeListAtom } from "@/shared/atoms";
import useBranch from "@/shared/atoms/useBranch";
import { useModalMeta } from "@/shared/atoms/useModal";
import useFranchiseHook from "@/shared/hooks/useFranchiseHook";
import useGenerateImageUrl from "@/shared/hooks/useGenerateImageUrl";
import useThemesHook from "@/shared/hooks/useThemesHook";
import useWebsiteSettingsHook from "@/shared/hooks/useWebsiteSettingsHook";
import useSchedulesQuery from "@/shared/queries/schedules/useSchedulesQuery";
import { errorToast, formatDate } from "@/shared/utils/helpers";
import { IThemeItem } from "@/shared/utils/interfaces";
import dayjs from "dayjs";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AllergenPosterBranch } from "../AllergenPoster/AllergenPoster";
import { getPosterColors } from "../AllergenPoster/allergenPosterUtils";
import BooleanWithLabel from "../Boolean/BooleanWithLabel";
import Button from "../Button/Button";
import Textfield from "../Input/Textfield";
import Modal, { ModalProps } from "../Modal/Modal";
import { WeekRosterPage } from "../ScheduleRoster/ScheduleRoster";
import {
  buildRosterPages,
  buildWorkerColors,
  defaultRosterOrientation,
  formatDatePart,
  formatHours,
  getRosterPageSize,
  getRosterRange,
  RosterMode,
  RosterOrientation,
  RosterSection,
  toRosterShifts,
} from "../ScheduleRoster/scheduleRosterUtils";
import Select from "../Select/Select";

const EMPTY_IMAGE_ID = "transpatentEmpty_sr5jbs";
const PREVIEW_SCALE = 0.6;

const slugify = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "restaurant";

const ScheduleRosterModal = ({ onExit, ...props }: ModalProps) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language || "nl";
  const [meta] = useModalMeta();
  useThemesHook();
  const themeList = useAtomValue(themeListAtom);
  const [franchiseId] = useFranchiseHook();
  const [branchDetails] = useBranch();
  const generateImageUrl = useGenerateImageUrl();
  const { websiteSettingsData } = useWebsiteSettingsHook({
    franchiseId,
    enabled: !!franchiseId,
  });

  const [mode, setMode] = useState<RosterMode>(meta?.mode ?? "week");
  const [orientation, setOrientation] = useState<RosterOrientation>(() =>
    defaultRosterOrientation(meta?.mode ?? "week")
  );
  // A week roster opened from the current month starts at today's week
  const [anchor, setAnchor] = useState(() => {
    const date = dayjs(meta?.date);
    if (!date.isValid() || date.isSame(dayjs(), "month")) return dayjs();
    return date.startOf("month");
  });
  const [customTitle, setCustomTitle] = useState<string | null>(null);
  const [selectedTheme, setSelectedTheme] = useState<IThemeItem | null>(null);
  const [showLogo, setShowLogo] = useState(true);
  const [showTotals, setShowTotals] = useState(true);
  const [showStaffing, setShowStaffing] = useState(true);
  const [showBlankLines, setShowBlankLines] = useState(true);
  const [hideOutsideDays, setHideOutsideDays] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  const { start, end } = useMemo(
    () => getRosterRange(mode, anchor),
    [mode, anchor]
  );
  const query = useSchedulesQuery({
    start_date_and_time: formatDate(start, "inputDate"),
    end_date_and_time: formatDate(end, "inputDate"),
  });

  // Preselect the theme that is active on the ordering website
  useEffect(() => {
    if (selectedTheme || !themeList?.length) return;
    const activeTheme = themeList.find(
      (theme) => theme.item_id === +websiteSettingsData?.color_template_id
    );
    if (activeTheme) setSelectedTheme(activeTheme);
  }, [themeList, websiteSettingsData, selectedTheme]);

  const logoUrl = useMemo(() => {
    const logo = websiteSettingsData?.logo;
    if (!showLogo || !logo || logo === EMPTY_IMAGE_ID || !franchiseId) {
      return null;
    }
    return generateImageUrl(logo, "logo", franchiseId);
  }, [websiteSettingsData, showLogo, franchiseId, generateImageUrl]);

  const branch: AllergenPosterBranch = useMemo(() => {
    const street = [
      branchDetails?.street_name,
      [branchDetails?.house_number, branchDetails?.house_number_extension]
        .filter(Boolean)
        .join(""),
    ]
      .filter(Boolean)
      .join(" ");
    const city = [branchDetails?.zip_code, branchDetails?.place]
      .filter(Boolean)
      .join(" ");

    return {
      name: branchDetails?.name ?? "",
      address: [street, city].filter(Boolean).join(", "),
    };
  }, [branchDetails]);

  const colors = useMemo(
    () => getPosterColors(selectedTheme ?? undefined),
    [selectedTheme]
  );

  // Only shifts that start inside the period, the API range is by day
  const shifts = useMemo(
    () =>
      toRosterShifts(query.data).filter(
        (shift) =>
          !shift.start.isBefore(start, "day") &&
          !shift.start.isAfter(end, "day")
      ),
    [query.data, start, end]
  );

  const workerColors = useMemo(() => buildWorkerColors(shifts), [shifts]);
  const pages = useMemo(
    () => buildRosterPages(shifts, start, end, orientation),
    [shifts, start, end, orientation]
  );
  const pageCount = pages.length;
  const pageSize = getRosterPageSize(orientation);

  // "Week 41 · 5 – 11 oktober 2026", limited to the days inside the period
  const weekLabel = (section: RosterSection) => {
    const active = section.days.filter((_, i) => section.activeDays[i]);
    const first = active[0] ?? section.days[0];
    const last = active[active.length - 1] ?? section.days[6];
    const from = first.isSame(last, "day")
      ? ""
      : `${formatDatePart(
          first,
          language,
          first.isSame(last, "month")
            ? { day: "numeric" }
            : { day: "numeric", month: "short" }
        )} – `;
    return `${t("scheduleRoster.weekNumber", {
      week: section.days[0].isoWeek(),
    })} · ${from}${formatDatePart(last, language, {
      day: "numeric",
      month: "long",
      year: "numeric",
    })}`;
  };

  const sectionSummary = (section: RosterSection) =>
    [
      t("scheduleRoster.employees", { count: section.rows.length }),
      t("scheduleRoster.shifts", { count: section.shiftCount }),
      showTotals &&
        t("scheduleRoster.hoursLong", {
          value: formatHours(section.totalMinutes, language),
        }),
    ]
      .filter(Boolean)
      .join("  ·  ");

  const navLabel =
    mode === "week" && pages[0]
      ? weekLabel(pages[0].section)
      : formatDatePart(start, language, { month: "long", year: "numeric" });

  const defaultTitle = t(
    mode === "week" ? "scheduleRoster.weekTitle" : "scheduleRoster.monthTitle"
  );
  const title = customTitle ?? defaultTitle;
  const generatedAt = dayjs().format("DD-MM-YYYY HH:mm");
  const fileName = `${t(
    mode === "week"
      ? "scheduleRoster.fileNameWeek"
      : "scheduleRoster.fileNameMonth"
  )}-${
    mode === "week"
      ? `${start.isoWeekYear()}-w${start.isoWeek()}`
      : start.format("YYYY-MM")
  }-${slugify(branch.name)}`;

  const renderPage = (index: number, ref?: (el: HTMLDivElement) => void) => {
    const page = pages[index];
    return (
      <WeekRosterPage
        ref={ref}
        rows={page.rows}
        section={page.section}
        sectionPageIndex={page.sectionPageIndex}
        isSectionEnd={page.isSectionEnd}
        showTotals={showTotals}
        pageIndex={index}
        pageCount={pageCount}
        orientation={orientation}
        colors={colors}
        title={title}
        period={weekLabel(page.section)}
        summary={sectionSummary(page.section)}
        branch={branch}
        logoUrl={logoUrl}
        generatedAt={generatedAt}
        workerColors={workerColors}
        language={language}
        showStaffing={showStaffing}
        showBlankLines={showBlankLines}
        hideInactiveDays={mode === "month" && hideOutsideDays}
      />
    );
  };

  const shiftPeriod = (direction: 1 | -1) =>
    setAnchor((current) => current.add(direction, mode));

  const buildPdf = async () => {
    const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
      import("html2canvas"),
      import("jspdf"),
    ]);
    const pdfOrientation = orientation === "portrait" ? "p" : "l";
    const pdf = new jsPDF(pdfOrientation, "mm", "a4");
    const [widthMm, heightMm] =
      orientation === "portrait" ? [210, 297] : [297, 210];

    // html2canvas measures the text baseline with a hidden <img> appended to
    // the body. Tailwind's `img { display: block }` pushes it to its own line,
    // which draws all text too low. Keep that probe inline while capturing.
    const baselineFix = document.createElement("style");
    baselineFix.textContent =
      'body > div[style*="visibility: hidden"] > img { display: inline !important; }';
    document.head.appendChild(baselineFix);

    try {
      for (let index = 0; index < pageCount; index++) {
        const element = pageRefs.current[index];
        if (!element) continue;
        const canvas = await html2canvas(element, {
          scale: 3,
          useCORS: true,
          backgroundColor: "#ffffff",
          width: pageSize.width,
          height: pageSize.height,
        });
        if (index > 0) pdf.addPage("a4", pdfOrientation);
        pdf.addImage(
          canvas.toDataURL("image/jpeg", 0.95),
          "JPEG",
          0,
          0,
          widthMm,
          heightMm
        );
      }
    } finally {
      baselineFix.remove();
    }

    return pdf;
  };

  const exportPdf = async (action: "download" | "print") => {
    setIsExporting(true);
    try {
      const pdf = await buildPdf();
      if (action === "download") {
        pdf.save(`${fileName}.pdf`);
      } else {
        pdf.autoPrint();
        window.open(pdf.output("bloburl"), "_blank");
      }
    } catch (error) {
      console.error(error);
      errorToast();
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal width={"120rem"} onExit={onExit} {...props}>
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex flex-col gap-5 lg:w-[32rem] lg:shrink-0">
          <div>
            <h2 className="text-[2rem] font-bold">
              {t("scheduleRoster.modalTitle")}
            </h2>
            <p className="mt-2 text-small text-text-tertiary">
              {t("scheduleRoster.modalDescription")}
            </p>
          </div>

          <div className="flex gap-2">
            {(["week", "month"] as RosterMode[]).map((option) => (
              <Button
                key={option}
                size="small"
                variant={mode === option ? "default" : "secondary"}
                onClick={() => {
                  setMode(option);
                  setOrientation(defaultRosterOrientation(option));
                }}
              >
                {t(
                  option === "week"
                    ? "scheduleRoster.weekButton"
                    : "scheduleRoster.monthButton"
                )}
              </Button>
            ))}
          </div>

          <div className="flex gap-2">
            {(["portrait", "landscape"] as RosterOrientation[]).map(
              (option) => (
                <Button
                  key={option}
                  size="small"
                  variant={orientation === option ? "default" : "secondary"}
                  onClick={() => setOrientation(option)}
                >
                  {t(`scheduleRoster.${option}`)}
                </Button>
              )
            )}
          </div>

          <div className="flex items-center justify-between gap-2 rounded-lg border border-border px-2 py-2">
            <Button
              variant="icon"
              icon="arrowLeft"
              aria-label={t("scheduleRoster.previous")}
              onClick={() => shiftPeriod(-1)}
            />
            <button
              type="button"
              className="cursor-pointer border-none bg-transparent text-center text-default font-semibold"
              title={t("scheduleRoster.today")}
              onClick={() => setAnchor(dayjs())}
            >
              {navLabel}
            </button>
            <Button
              variant="icon"
              icon="arrowRight"
              aria-label={t("scheduleRoster.next")}
              onClick={() => shiftPeriod(1)}
            />
          </div>

          <Textfield
            label={t("scheduleRoster.titleLabel")}
            value={title}
            onChange={(e) => setCustomTitle(e.target.value)}
          />

          <Select
            label={t("scheduleRoster.theme")}
            options={themeList}
            value={selectedTheme}
            isClearable
            placeholder={t("scheduleRoster.defaultTheme")}
            getOptionLabel={(option: IThemeItem) => option.template_name}
            getOptionValue={(option: IThemeItem) => `${option.item_id}`}
            onChange={(option: IThemeItem | null) => setSelectedTheme(option)}
          />

          <div className="flex gap-2">
            {[colors.primary, colors.accent].map((color, index) => (
              <span
                key={index}
                className="size-8 rounded-full border border-border"
                style={{ background: color }}
              />
            ))}
          </div>

          <BooleanWithLabel
            checked={showLogo}
            onChange={(e) => setShowLogo(e.target.checked)}
          >
            {t("scheduleRoster.showLogo")}
          </BooleanWithLabel>

          <BooleanWithLabel
            checked={showTotals}
            onChange={(e) => setShowTotals(e.target.checked)}
          >
            {t("scheduleRoster.showTotals")}
          </BooleanWithLabel>

          <BooleanWithLabel
            checked={showStaffing}
            onChange={(e) => setShowStaffing(e.target.checked)}
          >
            {t("scheduleRoster.showStaffing")}
          </BooleanWithLabel>

          <BooleanWithLabel
            checked={showBlankLines}
            onChange={(e) => setShowBlankLines(e.target.checked)}
          >
            {t("scheduleRoster.showBlankLines")}
          </BooleanWithLabel>

          {mode === "month" && (
            <BooleanWithLabel
              checked={hideOutsideDays}
              onChange={(e) => setHideOutsideDays(e.target.checked)}
            >
              {t("scheduleRoster.hideOutsideDays")}
            </BooleanWithLabel>
          )}

          <div className="flex flex-wrap gap-3">
            <Button
              icon="pdf"
              onClick={() => exportPdf("download")}
              isPending={isExporting}
              isDisabled={query.isPending}
            >
              {t("scheduleRoster.downloadPdf")}
            </Button>
            <Button
              variant="secondary"
              icon="printer"
              onClick={() => exportPdf("print")}
              isDisabled={query.isPending || isExporting}
            >
              {t("scheduleRoster.print")}
            </Button>
            <Button variant="tertiary" onClick={() => onExit()}>
              {t("close")}
            </Button>
          </div>
        </div>

        <div className="max-h-[75vh] flex-1 overflow-auto rounded-lg bg-[#e5e7eb] p-4">
          <div className="flex flex-col items-center gap-4">
            {Array.from({ length: pageCount }, (_, index) => (
              <div
                key={`${mode}-${orientation}-${index}`}
                className="shadow-lg"
                style={{
                  width: pageSize.width * PREVIEW_SCALE,
                  height: pageSize.height * PREVIEW_SCALE,
                  overflow: "hidden",
                  flexShrink: 0,
                  opacity: query.isPending ? 0.5 : 1,
                }}
              >
                <div
                  style={{
                    transform: `scale(${PREVIEW_SCALE})`,
                    transformOrigin: "top left",
                  }}
                >
                  {renderPage(index)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Unscaled copies used for the PDF capture, kept off screen */}
      <div
        aria-hidden
        style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none" }}
      >
        {Array.from({ length: pageCount }, (_, index) => (
          <div key={`${mode}-${orientation}-${index}`}>
            {renderPage(index, (element) => (pageRefs.current[index] = element))}
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default ScheduleRosterModal;
